import type { ReactElement } from 'react';
import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import type { SelectProps } from '@mui/material';
import { noop } from 'lodash';

import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import InputAdornment from '~/components/core/Atomic/InputAdornment';
import { hasOptionValue, useAutoSelectSingleOption } from '~/hooks/useAutoSelectSingleOption';

import type { TextFieldProps } from '../TextField';
import TextField from '../TextField';

export interface SingleSelectFieldProps<T> extends TextFieldProps<T> {
  clearSelection?: () => void;
  clearable?: boolean;
  SelectProps?: Partial<SelectProps>;
  children?: React.ReactNode;
  autoSelectSingleOption?: boolean;
}

const SingleSelectField = <T,>({
  id,
  clearable,
  value,
  disabled,
  clearSelection,
  children,
  SelectProps,
  InputProps,
  fullWidthField,
  onChange,
  autoSelectSingleOption = true,
  ...rest
}: SingleSelectFieldProps<T>): ReactElement => {
  // Children are MenuItems here, so derive the selectable (non-empty, non-disabled) values.
  const selectableValues = React.Children.toArray(children)
    .filter((child): child is React.ReactElement<{ value?: T; disabled?: boolean }> => React.isValidElement(child))
    .filter((child) => hasOptionValue(child.props.value) && !child.props.disabled)
    .map((child) => child.props.value as T);

  const isMultiple = !!SelectProps?.multiple;
  useAutoSelectSingleOption<T>({
    enabled: autoSelectSingleOption && !disabled && !!onChange,
    isEmpty: Array.isArray(value) ? !value.length : !hasOptionValue(value),
    options: selectableValues,
    onSelect: (option) =>
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error -- auto-select has no DOM event, and a multiple select commits an array
      onChange?.(isMultiple ? [option] : option, noop),
  });

  return (
    <TextField<T>
      value={value}
      onChange={onChange}
      disabled={disabled}
      InputProps={{
        id,
        ...(clearable
          ? {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    style={{
                      padding: 4,
                      visibility: value && !disabled ? 'visible' : 'hidden',
                      marginRight: 15,
                    }}
                    title="Clear"
                    onClick={() => (clearSelection ? clearSelection() : null)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }
          : {}),
        ...InputProps,
      }}
      select
      name={id}
      SelectProps={{
        ...SelectProps,
      }}
      fullWidthField={fullWidthField}
      {...rest}
    >
      {children}
    </TextField>
  );
};

export default SingleSelectField;
